import { fetchAuthSession } from 'aws-amplify/auth'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'
const isLocalAuthMode = import.meta.env.VITE_AUTH_MODE === 'local'

/**
 * Map shorthand names (e.g. "employee") to full token names (e.g. "employee-token").
 * Must stay in sync with AuthContext.tsx TOKEN_ALIASES.
 */
const TOKEN_ALIASES: Record<string, string> = {
  'super-admin': 'super-admin-token',
  'super_admin': 'super-admin-token',
  'admin': 'admin-token',
  'employee': 'employee-token',
  'viewer': 'viewer-token',
  'org2-admin': 'org2-admin-token',
}

function resolveLocalToken(): string {
  const raw = import.meta.env.VITE_AUTH_TOKEN || 'admin-token'
  return TOKEN_ALIASES[raw] ?? raw
}

/**
 * Mock token for local development.
 * Must match a predefined token in the backend's LocalAuthenticator.
 * Set via VITE_AUTH_TOKEN env var, defaults to admin-token.
 * Supports shorthand aliases (e.g. "employee" resolves to "employee-token").
 */
const LOCAL_MOCK_TOKEN = resolveLocalToken()

/**
 * localStorage key for the super_admin's currently-selected organization.
 * Synchronously hydrated at module init so the very first API request after
 * a page reload already carries the correct `org_id` query param.
 */
export const EFFECTIVE_ORG_ID_STORAGE_KEY = 'tt:effectiveOrgId'

function readStoredEffectiveOrgId(): string {
  if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') {
    return ''
  }
  try {
    return window.localStorage.getItem(EFFECTIVE_ORG_ID_STORAGE_KEY) ?? ''
  } catch {
    return ''
  }
}

function writeStoredEffectiveOrgId(orgId: string) {
  if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') {
    return
  }
  try {
    if (orgId) {
      window.localStorage.setItem(EFFECTIVE_ORG_ID_STORAGE_KEY, orgId)
    } else {
      window.localStorage.removeItem(EFFECTIVE_ORG_ID_STORAGE_KEY)
    }
  } catch {
    // storage unavailable (private mode, quota, etc.) — fall back to in-memory only
  }
}

let _effectiveOrgId = readStoredEffectiveOrgId()

/**
 * Set the effective org ID and persist it to localStorage. Use this for
 * explicit super_admin org selections where the value should survive a reload.
 * Passing an empty string clears both the in-memory value and the stored key.
 */
export function setEffectiveOrgId(orgId: string) {
  _effectiveOrgId = orgId
  writeStoredEffectiveOrgId(orgId)
}

/**
 * Update the in-memory effective org ID without touching localStorage. Use
 * this for non-persistent syncs (e.g. reflecting a non-super-admin user's own
 * orgId in the api module so their requests carry `org_id`) so we do not
 * pollute the super_admin selection key.
 */
export function setEffectiveOrgIdInMemory(orgId: string) {
  _effectiveOrgId = orgId
}

export function getEffectiveOrgId(): string {
  return _effectiveOrgId
}

/**
 * Custom error class for API errors with status code and body.
 */
export class ApiError extends Error {
  status: number
  body?: unknown

  constructor(message: string, status: number, body?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.body = body
  }
}

/**
 * Get the current JWT ID token from Amplify Auth.
 * Uses the ID token (not access token) because Cognito custom attributes
 * (custom:orgId, custom:role, name, email) are only present in ID tokens.
 * In local mode, returns a mock token that the backend accepts.
 */
async function getAuthToken(): Promise<string | null> {
  if (isLocalAuthMode) {
    return LOCAL_MOCK_TOKEN
  }

  try {
    const session = await fetchAuthSession()
    return session.tokens?.idToken?.toString() ?? null
  } catch {
    return null
  }
}

/**
 * Parse a non-ok Response into an ApiError and throw it. On 401 in
 * Cognito mode, redirect to /login before throwing so callers don't
 * need to duplicate that logic.
 *
 * The body parser tries JSON first and falls back to text — binary
 * error responses from API Gateway occasionally come through as text,
 * and multipart endpoints sometimes return plain strings for errors.
 *
 * Shared by the generic request() helper and by direct-fetch helpers
 * (downloadImportTemplate, postImportMultipart) that can't route
 * through request() because they handle Blob / FormData bodies.
 */
async function throwFromErrorResponse(response: Response): Promise<never> {
  let body: unknown
  try {
    body = await response.json()
  } catch {
    try {
      body = await response.text()
    } catch {
      body = undefined
    }
  }

  const message = getErrorMessage(response.status, body)
  const error = new ApiError(message, response.status, body)

  // In local auth mode, redirecting to /login causes an infinite loop
  // since the token is hardcoded and the login page has no form to
  // change it.
  if (response.status === 401 && !isLocalAuthMode) {
    window.location.href = '/login'
  }

  throw error
}

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

interface RequestOptions {
  /** Skip authentication header (for public endpoints) */
  noAuth?: boolean
  /** Custom headers to add to the request */
  headers?: Record<string, string>
  /** Abort signal for request cancellation */
  signal?: AbortSignal
}

/**
 * Make an authenticated API request.
 */
async function request<T>(
  method: HttpMethod,
  path: string,
  body?: unknown,
  options: RequestOptions = {}
): Promise<T> {
  let finalUrl = `${API_BASE_URL}${path}`
  if (_effectiveOrgId) {
    const separator = path.includes('?') ? '&' : '?'
    finalUrl += `${separator}org_id=${encodeURIComponent(_effectiveOrgId)}`
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...options.headers,
  }

  // Add auth header unless explicitly disabled
  if (!options.noAuth) {
    const token = await getAuthToken()
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }
  }

  const response = await fetch(finalUrl, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
    signal: options.signal,
  })

  // Handle error responses
  if (!response.ok) {
    await throwFromErrorResponse(response)
  }

  // Handle empty responses (204 No Content)
  if (response.status === 204) {
    return undefined as T
  }

  // Parse JSON response
  return response.json()
}

/**
 * Get a user-friendly error message from the response.
 */
function getErrorMessage(status: number, body: unknown): string {
  // Try to extract message from error body
  if (body && typeof body === 'object' && 'message' in body) {
    return String((body as { message: unknown }).message)
  }
  if (body && typeof body === 'object' && 'error' in body) {
    return String((body as { error: unknown }).error)
  }

  // Default messages by status code
  switch (status) {
    case 400:
      return 'Invalid request. Please check your input.'
    case 401:
      return 'Your session has expired. Please sign in again.'
    case 403:
      return 'You do not have permission to perform this action.'
    case 404:
      return 'The requested resource was not found.'
    case 409:
      return 'A conflict occurred. The resource may have been modified.'
    case 422:
      return 'Validation failed. Please check your input.'
    case 429:
      return 'Too many requests. Please try again later.'
    case 500:
      return 'An unexpected error occurred. Please try again later.'
    case 502:
    case 503:
    case 504:
      return 'Service temporarily unavailable. Please try again later.'
    default:
      return `Request failed with status ${status}`
  }
}

/**
 * Download the XLSX import template for the given org and trigger a
 * browser "Save as" prompt. Uses path-param orgId (not ?org_id=...)
 * because the import template route is explicitly scoped to the path.
 */
export async function downloadImportTemplate(orgId: string): Promise<void> {
  if (!orgId) {
    throw new ApiError('orgId is required', 400)
  }
  const token = await getAuthToken()
  const url = `${API_BASE_URL}/organizations/${encodeURIComponent(orgId)}/import-template.xlsx`

  // API Gateway only base64-decodes the Lambda response into raw binary
  // when the request's Accept header matches a configured BinaryMediaType.
  // The browser default (Accept: */*) does not, so we have to set it
  // explicitly here.
  const headers: Record<string, string> = {
    Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  }
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const response = await fetch(url, { method: 'GET', headers })
  if (!response.ok) {
    await throwFromErrorResponse(response)
  }

  const blob = await response.blob()
  const objectURL = URL.createObjectURL(blob)
  try {
    const a = document.createElement('a')
    a.href = objectURL
    a.download = 'tidy-tag-import-template.xlsx'
    document.body.appendChild(a)
    a.click()
    a.remove()
  } finally {
    // Revoke on next tick so the browser has time to issue the download.
    setTimeout(() => URL.revokeObjectURL(objectURL), 0)
  }
}

/**
 * POST the uploaded XLSX as multipart/form-data to the preview endpoint
 * and return the parsed preview JSON. Uses path-param orgId.
 */
export async function previewImport<T = unknown>(orgId: string, file: File): Promise<T> {
  return postImportMultipart<T>(orgId, file, 'preview')
}

/**
 * POST the uploaded XLSX as multipart/form-data to the apply endpoint
 * and return the parsed result JSON. Uses path-param orgId.
 */
export async function applyImport<T = unknown>(orgId: string, file: File): Promise<T> {
  return postImportMultipart<T>(orgId, file, 'apply')
}

async function postImportMultipart<T>(orgId: string, file: File, kind: 'preview' | 'apply'): Promise<T> {
  if (!orgId) {
    throw new ApiError('orgId is required', 400)
  }
  const token = await getAuthToken()
  const url = `${API_BASE_URL}/organizations/${encodeURIComponent(orgId)}/import/${kind}`

  const form = new FormData()
  // Backend handler extracts the "file" field; do NOT JSON.stringify.
  form.append('file', file)

  const headers: Record<string, string> = {}
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }
  // Deliberately omit Content-Type — fetch sets it to
  // multipart/form-data with the correct boundary when body is FormData.

  const response = await fetch(url, { method: 'POST', headers, body: form })
  if (!response.ok) {
    await throwFromErrorResponse(response)
  }

  return response.json()
}

/**
 * API client with methods for common HTTP operations.
 */
export const api = {
  /**
   * GET request
   */
  get<T>(path: string, options?: RequestOptions): Promise<T> {
    return request<T>('GET', path, undefined, options)
  },

  /**
   * POST request
   */
  post<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return request<T>('POST', path, body, options)
  },

  /**
   * PUT request
   */
  put<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return request<T>('PUT', path, body, options)
  },

  /**
   * PATCH request
   */
  patch<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return request<T>('PATCH', path, body, options)
  },

  /**
   * DELETE request
   */
  delete<T>(path: string, options?: RequestOptions): Promise<T> {
    return request<T>('DELETE', path, undefined, options)
  },
}
